import React, { useState } from 'react';
import { Experiment, Goal } from '@/types/experiment';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Beaker, Clock, CheckCircle, XCircle, Play, BarChart3, Edit, Code, Download, Settings } from 'lucide-react';
import { experimentService } from '@/services/experimentService';
import { toast } from '@/hooks/use-toast';
import { PrismCodeBlock, PrismCodeCollection } from './PrismCodeBlock';

interface ExperimentsListProps {
  experiments: Experiment[];
  goals: Goal[];
  onUpdateResults: (experimentId: string, results: Record<string, any>) => void;
  onExperimentClick: (experiment: Experiment) => void;
}

export const ExperimentsList: React.FC<ExperimentsListProps> = ({
  experiments,
  goals,
  onUpdateResults,
  onExperimentClick
}) => {
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [isCodeDialogOpen, setIsCodeDialogOpen] = useState(false);
  const [codeExperiment, setCodeExperiment] = useState<Experiment | null>(null);
  const [manualResults, setManualResults] = useState('');
  
  const getGoalName = (goalId?: string) => {
    if (!goalId) {
      return 'Unassigned';
    }
    const goal = goals.find(g => g.id === goalId);
    return goal ? goal.name : 'Unknown Goal';
  };

  const handleRunSimulation = async (experiment: Experiment) => {
    try {
      // Simulate API call with loading state
      toast({
        title: "Running Simulation",
        description: "Calling experiment API...",
      });

      const results = await experimentService.runExperimentAPI(experiment.id, experiment.parameters);
      onUpdateResults(experiment.id, results);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to run experiment simulation.",
        variant: "destructive",
      });
    }
  };

  const handleManualResults = () => {
    if (!selectedExperiment || !manualResults.trim()) return;

    try {
      const results = JSON.parse(manualResults);
      onUpdateResults(selectedExperiment.id, results);
      setIsResultsDialogOpen(false);
      setManualResults('');
      setSelectedExperiment(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON format for results.",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Play className="h-4 w-4 text-green-400" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-blue-400" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return <Beaker className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      running: 'bg-green-600 text-white',
      completed: 'bg-blue-600 text-white', 
      failed: 'bg-red-600 text-white',
      idle: 'bg-slate-600 text-white'
    };
    
    return (
      <Badge className={variants[status as keyof typeof variants] || variants.idle}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleShowCode = (experiment: Experiment) => {
    setCodeExperiment(experiment);
    setIsCodeDialogOpen(true);
  };

  if (experiments.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        <Beaker className="mx-auto h-12 w-12 text-slate-600 mb-4" />
        <p className="text-lg font-medium mb-2 text-slate-300">No experiments yet</p>
        <p className="text-sm">Create your first experiment to start tracking results</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-300">Status:</span>
          <select className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1 text-sm text-white">
            <option>All</option>
            <option>Running</option>
            <option>Completed</option>
            <option>Failed</option>
            <option>Idle</option>
          </select>
        </div>
        <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
          Reset
        </Button>
      </div>

      {/* Experiments Table */}
      <div className="space-y-3">
        {experiments.map((experiment) => (
          <div
            key={experiment.id}
            className="bg-slate-700 rounded-lg p-4 border border-slate-600 hover:border-slate-500 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {getStatusIcon(experiment.status)}
                  <h3 
                    className="font-medium text-white hover:text-blue-400 cursor-pointer transition-colors"
                    onClick={() => onExperimentClick(experiment)}
                  >
                    {experiment.name}
                  </h3>
                  {getStatusBadge(experiment.status)}
                </div>
                
                <p className="text-sm text-slate-400 mb-2">{experiment.hypothesis}</p>
                
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className={`flex items-center gap-1 ${!experiment.goalId ? 'text-amber-400' : ''}`}>
                    🎯 Goal: {getGoalName(experiment.goalId)}
                    {!experiment.goalId && <span className="text-amber-400">⚠️</span>}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Started: {new Date(experiment.createdAt).toLocaleDateString()}
                  </span>
                  {experiment.duration && (
                    <span>Duration: {experiment.duration}h</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShowCode(experiment)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-600"
                >
                  <Code className="h-4 w-4 mr-1" />
                  Code
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRunSimulation(experiment)}
                  disabled={experiment.status === 'running'}
                  className="border-slate-600 text-slate-300 hover:bg-slate-600"
                >
                  <Play className="h-4 w-4 mr-1" />
                  {experiment.status === 'running' ? 'Running...' : 'Run'}
                </Button>

                <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedExperiment(experiment)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-600"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Results
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader>
                      <DialogTitle>Add Manual Results</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Results (JSON format)</Label>
                        <Textarea
                          value={manualResults}
                          onChange={(e) => setManualResults(e.target.value)}
                          placeholder='{"metric1": 0.85, "metric2": 1200, "success": true}'
                          className="bg-slate-700 border-slate-600 text-white mt-2"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleManualResults}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        >
                          Save Results
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => setIsResultsDialogOpen(false)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {experiment.results && (
              <div className="mt-3 p-3 bg-slate-800 rounded border border-slate-600">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                  <span className="text-sm font-medium text-slate-300">Results</span>
                </div>
                <pre className="text-xs text-slate-400 overflow-x-auto">
                  {JSON.stringify(experiment.results, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Code Dialog */}
      <Dialog open={isCodeDialogOpen} onOpenChange={setIsCodeDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Python Code for "{codeExperiment?.name}"
            </DialogTitle>
          </DialogHeader>
          
          {codeExperiment && (
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground">
                Use these code snippets to interact with this experiment programmatically.
              </div>

              <PrismCodeCollection
                title="Python Client Code"
                description="Ready-to-use code snippets for your experiment"
              >
                <PrismCodeBlock
                  title="Get Experiment Details"
                  description="Fetch experiment information and status"
                  code={`from alphaloop_experiments import ExperimentsClient

# Initialize the client
alpha_loop = ExperimentsClient(api_key="ak_your_api_key_here")

# Get this experiment
experiment = alpha_loop.get_experiment("${codeExperiment.id}")
print(f"Experiment: {experiment['name']}")
print(f"Status: {experiment['status']}")
print(f"Parameters: {experiment['parameters']}")`}
                  icon={Download}
                  defaultExpanded={true}
                />

                <PrismCodeBlock
                  title="Update Experiment Parameters"
                  description="Modify experiment configuration"
                  code={`# Update parameters for this experiment
alpha_loop.update_experiment(
    experiment_id="${codeExperiment.id}",
    parameters=${JSON.stringify(codeExperiment.parameters || {}, null, 4)}
)`}
                  icon={Settings}
                />

                <PrismCodeBlock
                  title="Submit Results"
                  description="Send final experiment results"
                  code={`# Submit final results for this experiment
results = {
    "conversion_rate": 0.12,
    "engagement_score": 8.5,
    "cost_per_acquisition": 25.50,
    "total_impressions": 15000,
    "success": True
}

alpha_loop.submit_results(
    experiment_id="${codeExperiment.id}",
    results=results,
    result_type="final"
)

print("Results submitted successfully!")`}
                  icon={BarChart3}
                />

                <PrismCodeBlock
                  title="Stream Live Results (While Experiment is Running)"
                  description="Real-time progress updates"
                  code={`# For real-time results during experiment execution
def experiment_callback(day, metrics):
    alpha_loop.submit_results(
        experiment_id="${codeExperiment.id}",
        results={
            "day": day,
            "impressions": metrics["impressions"],
            "clicks": metrics["clicks"],
            "conversions": metrics["conversions"],
            "spend": metrics["spend"]
        },
        result_type="interim"
    )

# Use in your experiment monitoring loop
for day in range(experiment_duration):
    # Your experiment monitoring code here...
    daily_metrics = get_daily_metrics()
    experiment_callback(day, daily_metrics)`}
                  icon={Play}
                />
              </PrismCodeCollection>

              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-semibold mb-2">Experiment Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">ID:</span>
                    <code className="ml-2 bg-background px-2 py-1 rounded">{codeExperiment.id}</code>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <span className="ml-2">{codeExperiment.status}</span>
                  </div>
                  <div>
                    <span className="font-medium">Goal:</span>
                    <span className="ml-2">{getGoalName(codeExperiment.goalId)}</span>
                  </div>
                  <div>
                    <span className="font-medium">Created:</span>
                    <span className="ml-2">{new Date(codeExperiment.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
